import { Box, Button, Flex, Text } from '@chakra-ui/react';
import PropTypes from 'prop-types';
import { useMemo, useState } from 'react';
import ClientCohortSection from './ClientCohortSection';
import DownloadExcelButton from './DownloadExcelButton';
import DealIrrModelInputsPanel from './DealIrrModelInputsPanel';
import DealIrrModelOverlay from './DealIrrModelOverlay';
import DealIrrModelTable from './DealIrrModelTable';
import DealIrrSummarySection from './DealIrrSummarySection';
import { computeDealIrrModel } from './calc/computeDealIrrModel';
import { resolveDealIrrModelInputs } from './calc/resolveDealIrrModelInputs';
import { SCENARIO_META, SCENARIO_ORDER } from './calc/scenarioMeta';
import { makeRoasScenarioAdjuster } from './calc/applyRoasScenarioAdjustment';

function buildScenarioDealIrrState(state, scenarioKey) {
  if (scenarioKey === 'base') {
    return state;
  }
  const config = state.scenarios?.[scenarioKey];
  if (!config?.enabled) {
    return null;
  }
  const direction = scenarioKey === 'bull' ? 1 : -1;
  const adjustment = {
    enabled: true,
    mode: config.mode,
    value: config.value,
    direction,
  };
  const adjust = makeRoasScenarioAdjuster(adjustment);
  return {
    ...state,
    roas: adjust(state.roas),
    roasScenarioAdjustment: adjustment,
  };
}

function DealIrrModelResultsSection({
  state,
  setState,
  scenarioMap,
  calcResult,
  calcTimestamp,
  hillModel,
  useHillRoas,
  planMonths,
  spendRoasVectors,
  facilityFeeAmount,
  facilityFeeInstallmentCount,
  facilityFeePerInstallment,
  showClientCohort = false,
  onDownloadExcel,
}) {
  const [overlayOpen, setOverlayOpen] = useState(false);
  const [selectedScenario, setSelectedScenario] = useState('base');

  const activeScenarioKeys = useMemo(
    () =>
      SCENARIO_ORDER.filter((key) => {
        if (key === 'base') {
          return true;
        }
        return Boolean(state.scenarios?.[key]?.enabled);
      }),
    [state.scenarios]
  );

  const scenarioState = useMemo(
    () => buildScenarioDealIrrState(state, selectedScenario),
    [state, selectedScenario]
  );

  const dealIrrInputs = useMemo(
    () => resolveDealIrrModelInputs(scenarioState ?? state, hillModel),
    [scenarioState, state, hillModel]
  );

  const dealIrrModel = useMemo(() => {
    if (!dealIrrInputs) {
      return null;
    }
    const mode = useHillRoas && dealIrrInputs.hillMatched ? 'ltv' : 'roas';
    return computeDealIrrModel(dealIrrInputs, { mode });
  }, [dealIrrInputs, useHillRoas]);

  const summaryTitle = useHillRoas && dealIrrInputs?.hillMatched
    ? 'IRR Summary — LTV curve (Hill)'
    : 'IRR Summary — ROAS vector';

  return (
    <Box ref={null}>
      <Flex align="center" justify="space-between" gap={3} mb={4} flexWrap="wrap">
        <Box>
          <Text fontSize="xl" fontWeight="bold" className="brand">
            Pricing Model
          </Text>
          {calcTimestamp ? (
            <Text fontSize="xs" color="gray.400">
              Calculated {calcTimestamp.toLocaleTimeString()}
            </Text>
          ) : null}
        </Box>
        <Flex gap={2} align="center" flexWrap="wrap">
          {activeScenarioKeys.length > 1 ? (
            <>
              <Text fontSize="sm" color="gray.500">
                View scenario:
              </Text>
              {activeScenarioKeys.map((key) => (
                <Button
                  key={key}
                  size="sm"
                  variant={selectedScenario === key ? 'solid' : 'outline'}
                  colorScheme="purple"
                  onClick={() => setSelectedScenario(key)}
                >
                  {SCENARIO_META[key].label}
                </Button>
              ))}
            </>
          ) : null}
          {onDownloadExcel ? (
            <DownloadExcelButton onDownload={onDownloadExcel} size="sm" />
          ) : null}
          <Button size="sm" colorScheme="purple" onClick={() => setOverlayOpen(true)}>
            Edit
          </Button>
        </Flex>
      </Flex>

      <DealIrrModelInputsPanel
        state={state}
        outputs={dealIrrModel?.outputs ?? {}}
        facilityFeeAmount={facilityFeeAmount}
        variant="calculated"
        spreadCalculated
      />

      <Box className="card" p={4}>
        <DealIrrModelTable model={dealIrrModel} useHillRoas={useHillRoas} />
      </Box>

      <DealIrrModelOverlay
        isOpen={overlayOpen}
        onClose={() => setOverlayOpen(false)}
        state={state}
        setState={setState}
        planMonths={planMonths}
        useHillRoas={useHillRoas}
        dealIrrModel={dealIrrModel}
        facilityFeeAmount={facilityFeeAmount}
        facilityFeeInstallmentCount={facilityFeeInstallmentCount}
        hillModel={hillModel}
      />

      <DealIrrSummarySection summary={dealIrrModel?.summary} title={summaryTitle} />

      {showClientCohort && dealIrrModel ? (
        <ClientCohortSection
          dealIrrModel={dealIrrModel}
          dealIrrInputs={dealIrrInputs}
          state={state}
        />
      ) : null}
    </Box>
  );
}

DealIrrModelResultsSection.propTypes = {
  state: PropTypes.object.isRequired,
  setState: PropTypes.func.isRequired,
  scenarioMap: PropTypes.object,
  calcResult: PropTypes.object,
  calcTimestamp: PropTypes.instanceOf(Date),
  hillModel: PropTypes.object,
  useHillRoas: PropTypes.bool,
  planMonths: PropTypes.array.isRequired,
  spendRoasVectors: PropTypes.array,
  facilityFeeAmount: PropTypes.number.isRequired,
  facilityFeeInstallmentCount: PropTypes.number.isRequired,
  facilityFeePerInstallment: PropTypes.number.isRequired,
  showClientCohort: PropTypes.bool,
  onDownloadExcel: PropTypes.func,
};

export default DealIrrModelResultsSection;
